import { captureSupportScope, internalApiHeaders } from '../admin/supportTransport';
import type { CompanyContext, ProfileField } from '../company-context/api';

export const AI_PLANS_CHANGED = 'stratplan:ai-plans-changed';
export interface PlanChoice {
  id: string;
  label: string;
  description?: string;
}
export interface PlanQuestion {
  key: string;
  prompt: string;
  reason?: string;
  helperText?: string;
  field: ProfileField | string;
  scope: 'business_context' | 'run_preference';
  choices?: PlanChoice[];
  assumption?: string;
  affectedStages?: string[];
}
export interface PlanSettings {
  startDate: string;
  endDate: string;
  executionDays: number;
  emailAlerts: boolean;
}
export interface PlanPreparation {
  enabled: boolean;
  context: CompanyContext;
  canStart: boolean;
  questions: PlanQuestion[];
  recipientOptions: Array<{ id: string; name: string; email: string }>;
  defaultRecipientUserId?: string;
}
export interface PlanClarification extends PlanQuestion {
  id: string;
  runId?: string;
  status: string;
  answer?: string | { text?: string; answer?: string; choiceId?: string } | null;
}
export interface PlanArtifact {
  id: string;
  format: string;
  fileName: string;
  reportId?: string;
  status?: string;
}
export interface PlanRun {
  id: string;
  companyId: string;
  companyName?: string;
  status: string;
  jobKind?: 'generation' | 'revision' | 'exports';
  currentStage?: string | null;
  inputRevision: number;
  contextRevision: number;
  settings?: PlanSettings;
  costCents?: number;
  reservedCents?: number;
  budgetCents?: number;
  warnings?: string[];
  createdAt: string;
  updatedAt: string;
  steps?: Array<{ key: string; status: string; result?: unknown; error?: string | null }>;
  clarifications?: PlanClarification[];
  artifacts?: PlanArtifact[];
  reportId?: string | null;
  openQuestions?: number;
  parentRunId?: string | null;
  error?: string | null;
  errorCode?: string | null;
  canEditRun?: boolean;
  revisions?: Array<{ id: string; status: string; reportId?: string | null; createdAt: string }>;
  updatingReport?: boolean;
  latestRevisionId?: string | null;
  emailDeliveries?: Array<{
    id: string;
    status: string;
    attempts: number;
    acceptedAt?: string | null;
    deliveredAt?: string | null;
    lastError?: string | null;
    nextAttemptAt?: string | null;
  }>;
}
export interface PlanNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  actionUrl: string;
  createdAt: string;
  readAt: string | null;
  resolvedAt?: string | null;
}
export class PlanApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'PlanApiError';
  }
}
const base = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
const path = (value: string) =>
  `${base}${base.endsWith('/api') ? value.replace(/^\/api(?=\/)/, '') : value}`;
async function fetchPlan(value: string, options: RequestInit = {}) {
  const scope = captureSupportScope();
  const token = await (window as any).Clerk?.session?.getToken?.();
  if (!token) throw new PlanApiError('Sign in again to open your AI plans.', 401);
  const destination = path(value);
  const headers = internalApiHeaders(
    destination,
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
    false,
    scope
  );
  try {
    return await fetch(destination, { ...options, headers });
  } catch {
    throw new PlanApiError(
      'We couldn’t reach StratPlan. Your plan continues in the background.',
      0
    );
  }
}
async function request<T>(value: string, options?: RequestInit): Promise<T> {
  const response = await fetchPlan(value, options);
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.success)
    throw new PlanApiError(
      body?.error || 'AI plans are temporarily unavailable.',
      response.status,
      body?.code
    );
  return body.data as T;
}
const runs = '/api/ai-plan-runs';
const json = (body: unknown = {}) => ({ method: 'POST', body: JSON.stringify(body) });
export const planRunUrl = (companyId: string, runId?: string) =>
  `/company/${encodeURIComponent(companyId)}/ai-plan${runId ? `?run=${encodeURIComponent(runId)}` : ''}`;
export const planReportUrl = (run: PlanRun) =>
  `/company/${encodeURIComponent(run.companyId)}/visual-report${run.reportId ? `?reportId=${encodeURIComponent(run.reportId)}` : ''}`;
export const isPlanActive = (run: PlanRun) =>
  ['queued', 'running', 'revising', 'cancelling', 'waiting_for_input'].includes(run.status);
export const planApi = {
  list: (scope: { companyId?: string; practiceId?: string }) =>
    request<{ enabled: boolean; runs: PlanRun[] }>(
      `${runs}?${new URLSearchParams(Object.entries(scope).filter(([, value]) => value) as [string, string][]).toString()}`
    ),
  preparation: (companyId: string) =>
    request<PlanPreparation>(`${runs}/preparation/${encodeURIComponent(companyId)}`),
  get: (id: string) => request<PlanRun>(`${runs}/${encodeURIComponent(id)}`),
  start: (body: {
    companyId: string;
    expectedContextRevision: number;
    idempotencyKey: string;
    settings: PlanSettings;
    recipientUserId?: string;
  }) => request<PlanRun>(runs, json(body)),
  answer: (
    id: string,
    questionId: string,
    body: {
      expectedInputRevision: number;
      choiceId?: string;
      answer?: string;
      saveToContext?: false;
    }
  ) =>
    request<PlanRun>(
      `${runs}/${encodeURIComponent(id)}/clarifications/${encodeURIComponent(questionId)}/answer`,
      json(body)
    ),
  saveAnswerToContext: (id: string, questionId: string, expectedContextRevision: number) =>
    request<unknown>(
      `${runs}/${encodeURIComponent(id)}/clarifications/${encodeURIComponent(questionId)}/context`,
      json({ expectedContextRevision })
    ),
  cancel: (id: string) => request<PlanRun>(`${runs}/${encodeURIComponent(id)}/cancel`, json()),
  retry: (id: string) => request<PlanRun>(`${runs}/${encodeURIComponent(id)}/retry`, json()),
  refreshExports: (id: string, idempotencyKey: string) =>
    request<PlanRun>(`${runs}/${encodeURIComponent(id)}/refresh-exports`, json({ idempotencyKey })),
  notifications: () => request<PlanNotification[]>('/api/ai-plan-notifications'),
  markNotificationRead: (id: string) =>
    request<unknown>(`/api/ai-plan-notifications/${encodeURIComponent(id)}/read`, json()),
  download: async (runId: string, artifact: PlanArtifact) => {
    const response = await fetchPlan(
      `${runs}/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifact.id)}`
    );
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new PlanApiError(
        body?.error || 'The download could not be opened. Try again.',
        response.status
      );
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = artifact.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
